const WAVE_HEIGHTS = [34, 62, 88, 56, 96, 70, 44, 78, 100, 62, 40, 84, 58, 92, 48, 72, 38, 66, 90, 52];

export const Waveform = ({ bars = 20, className = 'h-9', tone = 'bg-brand-sky' }) => (
  <div className={`flex items-center gap-[3px] ${className}`} aria-hidden="true">
    {WAVE_HEIGHTS.slice(0, bars).map((h, i) => (
      <span
        key={i}
        className={`w-[3px] origin-center rounded-full ${tone}`}
        style={{
          height: `${h}%`,
          animation: `wavey ${0.9 + (i % 5) * 0.13}s ease-in-out ${i * 0.07}s infinite`,
        }}
      />
    ))}
  </div>
);

export const TranscriptBubble = ({ from = 'ai', children, time }) => (
  <div className={`flex ${from === 'ai' ? 'justify-start' : 'justify-end'}`}>
    <div
      className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] leading-snug ${
        from === 'ai'
          ? 'rounded-bl-md bg-brand-ice text-brand-body'
          : 'rounded-br-md bg-brand-navy text-white'
      }`}
    >
      {children}
      {time ? <span className="mt-1 block text-[10px] font-medium opacity-50">{time}</span> : null}
    </div>
  </div>
);

export const CallInterface = ({ className = '' }) => (
  <div
    className={`w-full overflow-hidden rounded-3xl border border-brand-lineBlue/70 bg-white shadow-lift ${className}`}
    data-testid="mock-call-interface"
  >
    <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
      <div className="flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
        </span>
        <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">Live call</span>
      </div>
      <div className="flex items-center gap-2 text-xs font-semibold text-slate-400">
        <span>CapableBee AI</span>
        <Waveform bars={8} className="h-4" />
      </div>
    </div>

    <div className="flex items-center gap-3 px-5 py-4">
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-ice text-sm font-extrabold text-brand-skyDeep">
        SM
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-bold text-brand-navy">Sarah Miller</p>
        <p className="text-xs text-slate-400">Incoming call · +1 (555) 014-2288</p>
      </div>
      <span className="ml-auto rounded-full bg-brand-honeySoft px-2.5 py-1 text-[11px] font-bold text-amber-700">
        New lead
      </span>
    </div>

    <div className="space-y-2.5 px-5 pb-5">
      <TranscriptBubble from="ai" time="0:03">
        Hi, thanks for calling! How can I help you today?
      </TranscriptBubble>
      <TranscriptBubble from="caller" time="0:09">
        I'd like to book a cleaning this week.
      </TranscriptBubble>
      <TranscriptBubble from="ai" time="0:15">
        You're in luck — we have Thursday at 2:30 PM. Shall I book it for you?
      </TranscriptBubble>
    </div>

    <div className="flex flex-wrap gap-2 border-t border-slate-100 bg-slate-50/70 px-5 py-3.5">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Appointment booked · Thu 2:30 PM
      </span>
      <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-lineBlue bg-brand-ice px-2.5 py-1 text-[11px] font-bold text-brand-skyDeep">
        <span className="h-1.5 w-1.5 rounded-full bg-brand-sky" /> Lead saved
      </span>
    </div>
  </div>
);

export const LeadCard = ({ name, email, tag = 'New lead', initials }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-brand-line bg-white p-4 shadow-card" data-testid="mock-lead-card">
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-ice text-xs font-extrabold text-brand-skyDeep">
      {initials}
    </div>
    <div className="min-w-0">
      <p className="truncate text-sm font-bold text-brand-navy">{name}</p>
      <p className="truncate text-xs text-slate-400">{email}</p>
    </div>
    <span className="ml-auto rounded-full bg-brand-honeySoft px-2.5 py-1 text-[10px] font-bold text-amber-700">
      {tag}
    </span>
  </div>
);

export const AppointmentCard = ({ title, meta, tone = 'sky' }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-brand-line bg-white p-4 shadow-card">
    <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tone === 'sky' ? 'bg-brand-ice text-brand-skyDeep' : 'bg-emerald-50 text-emerald-600'}`}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </svg>
    </div>
    <div>
      <p className="text-sm font-bold text-brand-navy">{title}</p>
      <p className="text-xs text-slate-400">{meta}</p>
    </div>
  </div>
);

export const MiniAnalytics = ({ className = '' }) => {
  const bars = [38, 56, 44, 72, 60, 86, 94];
  return (
    <div className={`rounded-2xl border border-brand-line bg-white p-4 shadow-card ${className}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Calls answered</p>
        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-600">24/7</span>
      </div>
      <div className="mt-3 flex h-20 items-end gap-2" aria-hidden="true">
        {bars.map((h, i) => (
          <div
            key={i}
            className={`flex-1 rounded-t-md ${i === bars.length - 1 ? 'bg-brand-sky' : 'bg-brand-ice'}`}
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      <div className="mt-3 flex items-end justify-between">
        <p className="text-2xl font-extrabold text-brand-navy">100%</p>
        <p className="text-xs text-slate-400">Leads captured automatically</p>
      </div>
    </div>
  );
};
